import { createSlice } from '@reduxjs/toolkit';
import { fetchTrackedOrder, searchOrders, searchPartnerOrders } from './trackingThunks';

/**
 * What the public tracking pages know.
 *
 * Nothing here is persisted, and that is on purpose: this is the one part of
 * the app used by somebody who is not our user, often on a borrowed or shared
 * phone. What they looked up should not outlive the tab.
 *
 * The search and the detail keep separate flags — the detail page is reached by
 * its own URL, so it loads with no search ever having run.
 */
const initialState = {
  results: [],
  /** True once a search has answered, so the page can tell "no results" from "not searched yet". */
  hasSearched: false,
  isSearching: false,
  searchError: null,

  order: null,
  /** The partner that manages the order, when it belongs to one. */
  admin: null,
  isLoadingOrder: false,
  orderError: null,
};

const trackingSlice = createSlice({
  name: 'tracking',
  initialState,
  reducers: {
    /** Switching the way you search drops what the previous one found. */
    clearSearch: (state) => {
      state.results = [];
      state.hasSearched = false;
      state.searchError = null;
    },
  },
  extraReducers: (builder) => {
    const searchPending = (state) => {
      state.isSearching = true;
      state.searchError = null;
      state.results = [];
    };
    const searchFulfilled = (state, action) => {
      state.isSearching = false;
      state.hasSearched = true;
      state.results = action.payload;
    };
    const searchRejected = (state, action) => {
      state.isSearching = false;
      state.hasSearched = true;
      state.results = [];
      state.searchError = action.payload;
    };

    builder
      .addCase(searchOrders.pending, searchPending)
      .addCase(searchOrders.fulfilled, searchFulfilled)
      .addCase(searchOrders.rejected, searchRejected)

      .addCase(searchPartnerOrders.pending, searchPending)
      .addCase(searchPartnerOrders.fulfilled, searchFulfilled)
      .addCase(searchPartnerOrders.rejected, searchRejected)

      .addCase(fetchTrackedOrder.pending, (state) => {
        state.isLoadingOrder = true;
        state.orderError = null;
      })
      .addCase(fetchTrackedOrder.fulfilled, (state, action) => {
        state.isLoadingOrder = false;
        state.order = action.payload.order;
        state.admin = action.payload.admin;
      })
      .addCase(fetchTrackedOrder.rejected, (state, action) => {
        state.isLoadingOrder = false;
        state.order = null;
        state.admin = null;
        state.orderError = action.payload;
      });
  },
});

export const { clearSearch } = trackingSlice.actions;

export default trackingSlice.reducer;
